/**
 * Single configuration point for the app theme.
 * All constants and storage keys live here.
 */

import { getPreferredColorScheme, themeIdForMode, type ThemeMode } from "./mode";

export const THEME_STORAGE_KEY = "softlavka-theme-id";
export const THEME_MARKETPLACE_STORAGE_KEY = "softlavka-themes-marketplace";

/** Ultimate registry fallback (also the dark half of the mode pair) */
export const DEFAULT_THEME_ID = "operator-dark";

/** Themes installed from the marketplace: { [themeId]: { bundleUrl, installedAt } } */
export interface InstalledMarketplaceTheme {
  bundleUrl: string;
  installedAt: string;
}

export function getStoredThemeId(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(THEME_STORAGE_KEY);
}

export function setStoredThemeId(themeId: string): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(THEME_STORAGE_KEY, themeId);
}

export const THEME_MODE_STORAGE_KEY = "softlavka-theme-mode";

export function getStoredThemeMode(): ThemeMode | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(THEME_MODE_STORAGE_KEY);
  return raw === "dark" || raw === "light" ? raw : null;
}

export function setStoredThemeMode(mode: ThemeMode): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
}

/**
 * First-run resolution: an explicitly stored theme always wins;
 * otherwise the OS color scheme picks the Operator/Clean Enterprise pair.
 */
export function getInitialThemeId(): string {
  const stored = getStoredThemeId();
  if (stored) return stored;
  return themeIdForMode(getPreferredColorScheme());
}

export function getInstalledMarketplaceThemes(): Record<string, InstalledMarketplaceTheme> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(THEME_MARKETPLACE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function setInstalledMarketplaceThemes(
  themes: Record<string, InstalledMarketplaceTheme>
): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(THEME_MARKETPLACE_STORAGE_KEY, JSON.stringify(themes));
}
